import { useRecoilState } from "recoil";
import { useCallback } from "react";
import { authState } from "../../State/Auth";
import { Config } from "../../../Config";
import { Result, err, ok } from ".././../Utils/Result";
import { ApiError, errorSchema } from "./Schemas";
import axios, { AxiosResponse } from "axios";
import { z } from "zod";

export const useHttpMethods = () => {
  const [auth] = useRecoilState(authState);

  const get = useCallback(async <T = Record<string, unknown>>(
    route: string,
    params?: Record<string, unknown>,
    schema?: z.ZodType<T, any, any>
  ): Promise<Result<T, ApiError>> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (auth.token) {
      headers["Authorization"] = `Bearer ${auth.token}`;
    }
    let response: AxiosResponse;
    try {
      response = await axios.get(Config.API_URL + route, {
        headers,
        params,
      });
    } catch (error: unknown) {
      console.error(error);
      if (!axios.isAxiosError(error) || !error.response) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR" });
      }

      const data = error.response.data as T;

      const parsedError = errorSchema.safeParse(data);

      if (!parsedError.success) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR", status: error.response.status });
      }

      return err({ ...parsedError.data, status: error.response.status });
    }

    const data = response.data as T;

    if (schema) {
      const parsedData = schema.safeParse(data);

      if (!parsedData.success) {
        console.error(parsedData.error);
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR" });
      }

      return ok(parsedData.data);
    }

    return ok(data);
  }, [auth.token]);

  const post = useCallback(async <T = Record<string, unknown>>(
    route: string,
    body?: Record<string, unknown>,
    schema?: z.ZodType<T, any, any>
  ): Promise<Result<T, ApiError>> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (auth.token) {
      headers["Authorization"] = `Bearer ${auth.token}`;
    }

    let response: AxiosResponse;
    try {
      response = await axios.post(Config.API_URL + route, body, {
        headers,
      });
    } catch (error: unknown) {
      console.error(error);
      if (!axios.isAxiosError(error) || !error.response) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR" });
      }

      const data = error.response.data as T;

      const parsedError = errorSchema.safeParse(data);

      if (!parsedError.success) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR", status: error.response.status });
      }

      return err({ ...parsedError.data, status: error.response.status });
    }

    const data = response.data as T;

    if (schema) {
      const parsedData = schema.safeParse(data);

      if (!parsedData.success) {
        console.error(parsedData.error);
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR" });
      }

      return ok(parsedData.data);
    }

    return ok(data);
  }, [auth.token]);

  const postWithFormData = useCallback(async <T = Record<string, unknown>>(
    route: string,
    formData?: FormData,
    schema?: z.ZodType<T, any, any>
  ): Promise<Result<T, ApiError>> => {
    const headers: Record<string, string> = {};

    if (auth.token) {
      headers["Authorization"] = `Bearer ${auth.token}`;
    }

    let response: AxiosResponse;
    try {
      response = await axios.post(Config.API_URL + route, formData, {
        headers,
      });
    } catch (error: unknown) {
      console.error(error);
      if (!axios.isAxiosError(error) || !error.response) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR" });
      }

      const data = error.response.data as T;

      const parsedError = errorSchema.safeParse(data);

      if (!parsedError.success) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR", status: error.response.status });
      }

      return err({ ...parsedError.data, status: error.response.status });
    }

    const data = response.data as T;

    if (schema) {
      const parsedData = schema.safeParse(data);

      if (!parsedData.success) {
        console.error(parsedData.error);
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR" });
      }

      return ok(parsedData.data);
    }

    return ok(data);
  }, [auth.token]);

  const del = useCallback(async <T = Record<string, unknown>>(
    route: string,
    params?: Record<string, unknown>,
    schema?: z.ZodType<T, any, any>
  ): Promise<Result<T, ApiError>> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (auth.token) {
      headers["Authorization"] = `Bearer ${auth.token}`;
    }

    let response: AxiosResponse;
    try {
      response = await axios.delete(Config.API_URL + route, {
        headers,
        params,
      });
    } catch (error: unknown) {
      console.error(error);
      if (!axios.isAxiosError(error) || !error.response) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR" });
      }

      const data = error.response.data as T;

      const parsedError = errorSchema.safeParse(data);

      if (!parsedError.success) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR", status: error.response.status });
      }

      return err({ ...parsedError.data, status: error.response.status });
    }

    const data = response.data as T;

    if (schema) {
      const parsedData = schema.safeParse(data);

      if (!parsedData.success) {
        console.error(parsedData.error);
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR" });
      }

      return ok(parsedData.data);
    }

    return ok(data);
  }, [auth.token]);

  const put = useCallback(async <T = Record<string, unknown>>(
    route: string,
    body?: Record<string, unknown>,
    schema?: z.ZodType<T, any, any>
  ): Promise<Result<T, ApiError>> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (auth.token) {
      headers["Authorization"] = `Bearer ${auth.token}`;
    }

    let response: AxiosResponse;
    try {
      response = await axios.put(Config.API_URL + route, body, {
        headers,
      });
    } catch (error: unknown) {
      console.error(error);
      if (!axios.isAxiosError(error) || !error.response) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR" });
      }

      const data = error.response.data as T;

      const parsedError = errorSchema.safeParse(data);

      if (!parsedError.success) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR", status: error.response.status });
      }

      return err({ ...parsedError.data, status: error.response.status });
    }

    const data = response.data as T;

    if (schema) {
      const parsedData = schema.safeParse(data);

      if (!parsedData.success) {
        console.error(parsedData.error);
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR" });
      }

      return ok(parsedData.data);
    }

    return ok(data);
  }, [auth.token]);

  const getBlob = useCallback(async (
    route: string,
    params?: Record<string, unknown>,
    extraHeaders: Record<string, string> = {},
  ): Promise<Result<Blob, ApiError>> => {
    const headers: Record<string, string> = {};
    if (auth.token) {
      headers["Authorization"] = `Bearer ${auth.token}`;
    }
    Object.assign(headers, extraHeaders);
    try {
      const response = await axios.get(Config.API_URL + route, {
        headers,
        params,
        responseType: 'blob',
      });
      return ok(response.data as Blob);
    } catch (error: unknown) {
      if (!axios.isAxiosError(error) || !error.response) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR" });
      }
      const parsedError = errorSchema.safeParse(error.response.data);
      if (!parsedError.success) {
        return err({ message: "Unknown error", errorCode: "UNKNOWN_ERROR", status: error.response.status });
      }
      return err({ ...parsedError.data, status: error.response.status });
    }
  }, [auth.token]);

  return {
    get,
    post,
    postWithFormData,
    del,
    put,
    getBlob,
  };
};
